import type { SortChangedEvent } from "@performyard/py-components";
import type { CompetencyAssignmentJSON, Level } from "@performyard/service-contracts";

import type { TeamCompetencyAssignmentSortBy } from "../../api/services/defs/competency-assignments";

// A stored rank never renders blank. Archived rows keep an expected_level that a later level-list
// shrink removed, and current_level is never cleared at all, so the name can be gone while the value
// is real. null is reserved for a genuinely absent rank — it's what empties the cell. Kept in step
// with the CSV export's resolveLevelName so the download matches what the grid showed.
export const resolveLevelName = (
	levels: Pick<Level, "rank" | "name">[] | undefined,
	rank: number | null | undefined
): string | null => {
	if (rank === null || rank === undefined) return null;
	return levels?.find((l) => l.rank === rank)?.name ?? `Level ${rank}`;
};

// An absent rank is offered as a labelled option rather than a clearable input, so setting a level
// one row at a time and setting it in bulk read the same way. 0 is out of band — a real rank is >= 1.
export const UNSET_LEVEL_VALUE = 0;
export const NOT_ASSESSED_LABEL = "Not assessed";
export const NO_EXPECTED_LEVEL_LABEL = "No expected level";

export const levelSelectOptions = (
	levels: Pick<Level, "rank" | "name">[],
	unsetLabel: string
): { value: number; label: string }[] => [
	{ value: UNSET_LEVEL_VALUE, label: unsetLabel },
	...[...levels].sort((a, b) => a.rank - b.rank).map((l) => ({ value: l.rank, label: `${l.name} (${l.rank})` })),
];

export const rankToSelectValue = (rank: number | null | undefined): number => rank ?? UNSET_LEVEL_VALUE;

export const selectValueToRank = (value: number | null | undefined): number | null =>
	value === UNSET_LEVEL_VALUE || value === null || value === undefined ? null : value;

export const PAGE_SIZE = 25;

// Sort mapping for the shared CompetenciesTable when the Assignee column is present (team + company pages).
export const colIdToSortBy = (colId: string | undefined): TeamCompetencyAssignmentSortBy | null => {
	if (
		colId === "assignee_name" ||
		colId === "competency_name" ||
		colId === "expected_level" ||
		colId === "current_level"
	)
		return colId;
	// "role_name" is intentionally absent — the Role column is not sortable (Pitstop-4 decision).
	return null;
};

export type CompetencySort = { name: string; direction: "asc" | "desc" };

// The competencies tables sort by a single column; pull it out of the grid's sort event.
export const competencySortFromEvent = (event: SortChangedEvent<CompetencyAssignmentJSON>): CompetencySort | null => {
	const sortedColumn = event.api.getColumnState().find((column) => column.sort);
	if (!sortedColumn?.colId || !sortedColumn.sort) return null;
	return { name: sortedColumn.colId, direction: sortedColumn.sort };
};
